import { useEffect, useState } from "react";

const Graph = ({ nodes, traversedNode, backtrackingNode, visitedNodes = [] }) => {
  const [nodePositions, setNodePositions] = useState({});
  const nodeSize = 50;
  const centerX = 600;
  const centerY = 60;
  const verticalSpacing = 110;

  useEffect(() => {
    if (Object.keys(nodes).length === 0) return;

    const positions = {};
    const levels = {};
    const rootNode = Object.keys(nodes)[0];

    const queue = [{ node: rootNode, level: 0 }];
    const visited = new Set([rootNode]);

    while (queue.length > 0) {
      const { node, level } = queue.shift();
      if (!levels[level]) levels[level] = [];
      levels[level].push(node);

      nodes[node]?.forEach((child) => {
        if (!visited.has(child)) {
          visited.add(child);
          queue.push({ node: child, level: level + 1 });
        }
      });
    }

    Object.keys(levels).forEach((level) => {
      const nodesInLevel = levels[level];
      const levelWidth = nodesInLevel.length * 120;
      const startX = centerX - levelWidth / 2 + 60;
      nodesInLevel.forEach((node, index) => {
        positions[node] = {
          x: startX + index * 120,
          y: centerY + Number.parseInt(level) * verticalSpacing,
        };
      });
    });

    setNodePositions(positions);
  }, [nodes]);

  const getNodeFill = (node) => {
    if (traversedNode === node)   return "#ef4444";
    if (backtrackingNode === node) return "#f59e0b";
    if (visitedNodes.includes(node)) return "#1e3a5f";
    return "rgba(13, 27, 42, 0.9)";
  };

  const getNodeStroke = (node) => {
    if (traversedNode === node)    return "#fca5a5";
    if (backtrackingNode === node)  return "#fcd34d";
    if (visitedNodes.includes(node)) return "#60a5fa";
    return "rgba(255, 255, 255, 0.2)";
  };

  const getTextFill = (node) => {
    if (traversedNode === node)    return "#ffffff";
    if (backtrackingNode === node)  return "#ffffff";
    if (visitedNodes.includes(node)) return "#93c5fd";
    return "rgba(255, 255, 255, 0.45)";
  };

  const renderEdges = () => {
    if (Object.keys(nodePositions).length === 0) return null;
    return Object.keys(nodes).flatMap((node) => {
      if (!nodes[node]) return [];
      return nodes[node]
        .map((child) => {
          if (!nodePositions[node] || !nodePositions[child]) return null;
          return (
            <line
              key={`${node}-${child}`}
              x1={nodePositions[node].x}
              y1={nodePositions[node].y + nodeSize / 2}
              x2={nodePositions[child].x}
              y2={nodePositions[child].y - nodeSize / 2}
              stroke="rgba(255, 255, 255, 0.15)"
              strokeWidth={1.5}
            />
          );
        })
        .filter(Boolean);
    });
  };

  const renderNodes = () => {
    return Object.keys(nodePositions).map((node) => (
      <g key={node}>
        <circle
          cx={nodePositions[node].x}
          cy={nodePositions[node].y}
          r={nodeSize / 2}
          fill={getNodeFill(node)}
          stroke={getNodeStroke(node)}
          strokeWidth={traversedNode === node || backtrackingNode === node ? 3 : 2}
          style={{ transition: "fill 0.3s ease, stroke 0.3s ease" }}
        />
        <text
          x={nodePositions[node].x}
          y={nodePositions[node].y}
          dy=".35em"
          textAnchor="middle"
          fontSize="16"
          fontWeight="600"
          fill={getTextFill(node)}
          style={{ transition: "fill 0.3s ease" }}
        >
          {node}
        </text>
      </g>
    ));
  };

  return (
    <svg width="100%" height="100%" style={{ minHeight: 500 }}>
      {renderEdges()}
      {renderNodes()}
      {Object.keys(nodePositions).length === 0 && (
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          fill="rgba(255,255,255,0.2)"
          fontSize="15"
          fontWeight="500"
        >
          Add nodes to visualize the graph
        </text>
      )}
    </svg>
  );
};

export default Graph;
